import { useState } from 'react';
import moment from 'moment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Popover, PopoverTrigger, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { Calendar } from 'root/src/client/v2/common/components/ui/calendar';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const triggerClass = 'flex w-full items-center justify-between gap-2 rounded-lg border border-solid border-white/[0.06] bg-white/[0.03] px-3 py-2 text-sm transition-colors hover:border-white/20 focus:border-primary/30 focus:outline-none';
const timeInputClass = 'w-full rounded-lg border border-solid border-white/[0.06] bg-white/[0.03] px-3 py-2 text-sm text-foreground [color-scheme:dark] focus:border-primary/30 focus:outline-none';
const startOfDay = (date) => { const next = new Date(date); next.setHours(0, 0, 0, 0); return next; };
const endOfDay = (date) => { const next = new Date(date); next.setHours(23, 59, 59, 999); return next; };
// The Calendar and the native time input both work in local time, so UTC mode round-trips through
// a "display date": a local Date whose local fields hold the UTC wall clock of the real instant.
const ISO_LOCAL = 'YYYY-MM-DDTHH:mm:ss';
const toDisplayDate = (iso, utc) => (utc ? new Date(moment.utc(iso).format(ISO_LOCAL)) : new Date(iso));
const fromDisplayDate = (date, utc) => (utc ? moment.utc(moment(date).format(ISO_LOCAL)).toISOString() : date.toISOString());
// shadcn datetime recipe: Popover + Calendar (date) + native time input. `value`/`onChange` are ISO.
export const DateTimePicker = ({ value, onChange, min, max, placeholder = 'Pick a date & time', id, utc = false }) => {
    const [open, setOpen] = useState(false);
    const selected = value ? toDisplayDate(value, utc) : undefined;
    const time = selected ? moment(selected).format('HH:mm') : '12:00';
    const emit = (date, hhmm) => {
        const [hours, minutes] = hhmm.split(':').map(Number);
        const next = new Date(date);
        next.setHours(hours || 0, minutes || 0, 0, 0);
        onChange(fromDisplayDate(next, utc));
    };
    return (
    // `modal` is required: several call sites render this picker inside a modal Radix Dialog,
    // which sets `pointer-events: none` on <body> and locks touch input to the dialog subtree.
    // A non-modal popover portals to <body>, so on touch devices taps on the calendar are
    // swallowed and the picker appears dead. A modal popover mounts its own dismissable layer
    // and stays interactive in both contexts (inside dialogs and on plain pages).
    <Popover open={open} onOpenChange={setOpen} modal>
			<PopoverTrigger asChild>
				<button id={id} type="button" className={cn(triggerClass, 'min-w-0', selected ? 'text-foreground' : 'text-muted-foreground/50')}>
					<span className="truncate">{selected ? `${moment(selected).format('ddd, MMM D YYYY HH:mm')}${utc ? ' UTC' : ''}` : placeholder}</span>
					<CalendarMonthIcon sx={{ fontSize: 16 }} className="shrink-0 text-muted-foreground"/>
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="redesign w-auto p-0">
				<Calendar mode="single" selected={selected} defaultMonth={selected !== null && selected !== void 0 ? selected : min} onSelect={(date) => { if (date) {
        emit(date, time);
    } }} disabled={date => Boolean((min && date < startOfDay(min)) || (max && date > endOfDay(max)))} initialFocus/>
				<div className="border-t border-solid border-white/[0.06] p-3">
					<label className="mb-1.5 block text-[11px] font-medium text-muted-foreground">{utc ? 'Time (UTC)' : 'Time'}</label>
					<input type="time" value={time} onChange={event => { var _a; return emit((_a = selected !== null && selected !== void 0 ? selected : min) !== null && _a !== void 0 ? _a : new Date(), event.target.value); }} className={timeInputClass}/>
				</div>
			</PopoverContent>
		</Popover>);
};
